import { makeRouter } from '@adornis/router/client/make-router.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html } from 'lit';
import { confirmDialog } from './confirm-dialog.js';
import { showImprint, showPrivacyPolicy } from './legal-dialog.js';
import { AdminDashboard } from './pages/admin-dashboard.js';
import { AdminLogin } from './pages/admin-login.js';
import { CampaignDetail } from './pages/campaign-detail.js';
import { CampaignWizard } from './pages/campaign-wizard.js';
import { Connected } from './pages/connected.js';
import { Dashboard } from './pages/dashboard.js';
import { OptOutPage } from './pages/opt-out.js';
import { Settings } from './pages/settings.js';

const navTabs = [
  { key: 'dashboard', label: 'Dashboard', icon: 'space-dashboard' },
  { key: 'newCampaign', label: 'New Campaign', icon: 'add-circle' },
  { key: 'settings', label: 'Settings', icon: 'settings' },
] as const;

// Per-route document title (used for browser tab + screen-reader announcement)
const PAGE_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  newCampaign: 'New Campaign',
  editCampaign: 'Edit Campaign',
  campaignDetail: 'Campaign',
  settings: 'Settings',
  adminLogin: 'Admin Login',
  admin: 'Admin',
  connected: 'Connected',
  optOut: 'Unsubscribe',
};

const APP_NAME = 'NDI AI SDR';

let lastTitledRoute: string | null = null;

function applyRouteSideEffects(activeRoute: string) {
  // Update document title (skip if same route to avoid noisy SR announcements during re-renders)
  if (lastTitledRoute !== activeRoute) {
    const pageTitle = PAGE_TITLES[activeRoute] ?? '';
    document.title = pageTitle ? `${pageTitle} · ${APP_NAME}` : APP_NAME;
    lastTitledRoute = activeRoute;

    // Scroll to top + move focus to the new page's <h1> for keyboard / SR users.
    requestAnimationFrame(() => {
      const main = document.getElementById('main-content');
      if (main) main.scrollTo({ top: 0, behavior: 'auto' });
      window.scrollTo({ top: 0, behavior: 'auto' });
      const h1 = (main ?? document).querySelector<HTMLElement>('h1');
      if (h1) {
        if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
        try {
          h1.focus({ preventScroll: true });
        } catch {
          /* ignore */
        }
      }
    });
  }
}

async function handleLogout() {
  if (
    !(await confirmDialog('Sign out of NDI AI SDR?', {
      title: 'Sign Out',
      confirmLabel: 'Sign Out',
      confirmClass: 'btn-primary',
    }))
  )
    return;
  logout();
}

export const sdrRouter = makeRouter(
  {
    dashboard: { path: '/', render: () => Dashboard() },
    newCampaign: { path: '/campaign/new', render: () => CampaignWizard({}) },
    editCampaign: {
      path: '/campaign/:campaignId/edit',
      render: (params: Record<string, string | undefined>) => {
        const id = params.campaignId?.trim();
        if (!id)
          return html`<p class="text-error p-6">
            Invalid campaign ID. <a class="link" @click=${() => sdrRouter.goTo('dashboard')}>Back to Dashboard</a>
          </p>`;
        return CampaignWizard({ campaignId: id });
      },
    },
    campaignDetail: {
      path: '/campaign/:campaignId/detail',
      render: (params: Record<string, string | undefined>) => {
        const id = params.campaignId?.trim();
        if (!id)
          return html`<p class="text-error p-6">
            Invalid campaign ID. <a class="link" @click=${() => sdrRouter.goTo('dashboard')}>Back to Dashboard</a>
          </p>`;
        return CampaignDetail({ campaignId: id });
      },
    },
    settings: { path: '/settings', render: () => Settings() },
    optOut: { path: '/optout', render: () => OptOutPage() },
    adminLogin: { path: '/admin/login', render: () => AdminLogin() },
    admin: { path: '/admin', render: () => AdminDashboard() },
    connected: { path: '/connected', render: () => Connected() },
  },
  {
    routerName: 'sdr',
    fallback: () => {
      // Unknown route (e.g. /campaign//edit) → fix browser URL and render dashboard directly
      console.warn('[sdr-router] Unknown route, showing dashboard:', location.pathname);
      if (location.pathname !== '/') {
        history.replaceState(null, '', '/');
      }
      return Dashboard();
    },
    outletWrapper: content => {
      const active = sdrRouter.currentRoute.value.route.name;

      // Apply title / scroll / focus side-effects on every route change.
      applyRouteSideEffects(active);

      // Admin and opt-out pages have their own layout — skip the main app wrapper
      if (active === 'admin' || active === 'adminLogin' || active === 'optOut') {
        return content;
      }

      return html`
        <div class="bg-base-200 flex h-screen flex-col">
          <a
            href="#main-content"
            class="focus:bg-primary focus:text-primary-content sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[12000] focus:rounded focus:px-3 focus:py-2 focus:shadow-lg"
            @click=${(e: Event) => {
              e.preventDefault();
              const main = document.getElementById('main-content');
              const h1 = main?.querySelector<HTMLElement>('h1');
              (h1 ?? main)?.focus();
            }}
            >Skip to main content</a
          >

          <nav class="navbar ndi-nav shadow-md" aria-label="Main navigation">
            <div class="flex-1">
              <a
                class="flex cursor-pointer items-center gap-2 px-4"
                @click=${() => sdrRouter.goTo('dashboard')}
                aria-label="Go to dashboard"
              >
                <img src="/ndi-logo.png" alt="" aria-hidden="true" class="h-7 w-auto" />
                <span class="text-sm font-semibold tracking-wide opacity-90">AI SDR</span>
              </a>
            </div>
            <div class="flex gap-1" role="tablist">
              ${navTabs.map(
                tab => html`
                  <button
                    class="btn btn-ghost btn-sm ${active === tab.key ? 'btn-active' : ''} gap-1.5"
                    role="tab"
                    aria-selected=${active === tab.key ? 'true' : 'false'}
                    aria-current=${active === tab.key ? 'page' : 'false'}
                    title=${tab.label}
                    @click=${() => sdrRouter.goTo(tab.key)}
                  >
                    <span class="icon-[material-symbols--${tab.icon}] text-lg" aria-hidden="true"></span>
                    <span class="hidden sm:inline">${tab.label}</span>
                  </button>
                `,
              )}
            </div>
            <div class="flex-none px-2">
              <div class="dropdown dropdown-end">
                <button tabindex="0" class="btn btn-ghost btn-sm btn-square" aria-label="User menu" title="Account">
                  <span class="icon-[material-symbols--account-circle] text-2xl" aria-hidden="true"></span>
                </button>
                <ul
                  tabindex="0"
                  class="dropdown-content menu menu-sm bg-base-100 text-base-content rounded-box z-[1] mt-2 w-44 p-2 shadow-lg"
                >
                  <li>
                    <a @click=${() => sdrRouter.goTo('settings')}>
                      <span class="icon-[material-symbols--settings] text-base" aria-hidden="true"></span>
                      Settings
                    </a>
                  </li>
                  <li>
                    <a class="text-error" @click=${() => void handleLogout()}>
                      <span class="icon-[material-symbols--logout] text-base" aria-hidden="true"></span>
                      Sign Out
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
          <main id="main-content" tabindex="-1" class="flex-1 overflow-auto outline-none">${content}</main>
          <footer
            class="bg-base-100 border-base-300 text-base-content/70 flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-xs"
            aria-label="Legal"
          >
            <span>© ${new Date().getFullYear()} New Digital Intelligence GmbH</span>
            <nav class="flex items-center gap-3" aria-label="Legal links">
              <button type="button" class="link link-hover" @click=${() => void showPrivacyPolicy()}>
                Datenschutzerklärung
              </button>
              <span aria-hidden="true">·</span>
              <button type="button" class="link link-hover" @click=${() => void showImprint()}>Impressum</button>
            </nav>
          </footer>
        </div>
      `;
    },
  },
);
